import { useEffect, useRef, useState } from "react";
import india from "@svg-maps/india";

// Real political map of India with per-state pledge counts. Counts sit on the
// state itself; for states too small to hold a number (Delhi, Goa, the
// north-east cluster, …) the number is placed outside with a leader line.

const VIEW_W = 612;
const VIEW_H = 696;

// State names in our data that differ from (or are missing in) the map.
const NAME_ALIASES = {
  "ladakh": "jammu and kashmir",
  "dadra and nagar haveli and daman and diu": "dadra and nagar haveli",
};

// Small states: label offset [dx, dy] from the state's centre, tuned per
// geography so numbers sit in open space with a leader line back.
const OUTSIDE_LABELS = {
  dl: [-52, -18], // Delhi -> left
  ch: [-48, -34], // Chandigarh -> upper left
  sk: [6, -42],   // Sikkim -> above
  ga: [-44, 12],  // Goa -> into the sea (west)
  py: [46, 16],   // Puducherry -> east coast
  ml: [-36, 40],  // Meghalaya -> below-left
  tr: [-8, 46],   // Tripura -> below
  mz: [34, 40],   // Mizoram -> below-right
  mn: [48, -6],   // Manipur -> right
  nl: [48, -18],  // Nagaland -> right
  dn: [-48, -6],  // Dadra & Nagar Haveli -> west
  dd: [-48, 14],  // Daman & Diu -> west
  ld: [34, -8],   // Lakshadweep -> right of the islands
  an: [-42, -10], // Andaman & Nicobar -> left
};

export function IndiaStateCountMap({ breakdown = [], dark = false }) {
  const counts = {};
  breakdown.forEach((b) => {
    const key = (b.state || "").toLowerCase();
    const mapped = NAME_ALIASES[key] || key;
    counts[mapped] = (counts[mapped] || 0) + b.count;
  });
  const max = Math.max(1, ...Object.values(counts));

  // Measure each state's bounding box once the paths exist in the DOM.
  const pathRefs = useRef({});
  const [centers, setCenters] = useState(null);
  useEffect(() => {
    const c = {};
    for (const [id, el] of Object.entries(pathRefs.current)) {
      if (!el) continue;
      const b = el.getBBox();
      c[id] = { x: b.x + b.width / 2, y: b.y + b.height / 2 };
    }
    setCenters(c);
  }, []);

  const idle = dark ? "#16233f" : "#eef2f7";
  const border = dark ? "#2a3a5f" : "#cbd5e1";
  const labelColor = dark ? "#ffffff" : "#0f172a";
  const leader = dark ? "#94a3b8" : "#64748b";

  return (
    <div data-testid="india-state-count-map" className="w-full flex justify-center">
      <svg
        viewBox={`-70 -10 ${VIEW_W + 140} ${VIEW_H + 20}`}
        className="w-full max-w-lg h-auto"
        role="img"
        aria-label="Pledge counts by state on the map of India"
      >
        {india.locations.map((loc) => {
          const count = counts[loc.name.toLowerCase()] || 0;
          const active = count > 0;
          const intensity = active ? 0.35 + 0.65 * (count / max) : 0;
          return (
            <path
              key={loc.id}
              ref={(el) => { pathRefs.current[loc.id] = el; }}
              d={loc.path}
              fill={active ? `rgba(0, 86, 210, ${intensity})` : idle}
              stroke={active ? "#FF9933" : border}
              strokeWidth={active ? 1.1 : 0.7}
            >
              <title>{`${loc.name}: ${count}`}</title>
            </path>
          );
        })}

        {/* count labels */}
        {centers && india.locations.map((loc) => {
          const count = counts[loc.name.toLowerCase()] || 0;
          if (!count) return null;
          const c = centers[loc.id];
          if (!c) return null;
          const outside = OUTSIDE_LABELS[loc.id];
          const lx = outside ? c.x + outside[0] : c.x;
          const ly = outside ? c.y + outside[1] : c.y;
          return (
            <g key={`label-${loc.id}`} pointerEvents="none">
              {outside && (
                <line x1={c.x} y1={c.y} x2={lx} y2={ly - 4} stroke={leader} strokeWidth="0.9" />
              )}
              {outside && (
                <circle cx={c.x} cy={c.y} r="1.6" fill={leader} />
              )}
              <text
                x={lx}
                y={ly}
                textAnchor="middle"
                dominantBaseline="central"
                fontSize={count >= 1000 ? 11 : 13}
                fontWeight="800"
                fill={labelColor}
                fontFamily="Outfit, sans-serif"
                stroke={dark ? "rgba(2,6,23,0.85)" : "rgba(255,255,255,0.9)"}
                strokeWidth="2.6"
                paintOrder="stroke"
              >
                {count.toLocaleString("en-IN")}
              </text>
            </g>
          );
        })}
      </svg>
    </div>
  );
}
